import React, { useState, useRef } from "react";
import {
    Box, Button, TextField, Typography, Container,
    Stack, CircularProgress, Alert, IconButton,
    InputAdornment, Fade
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from "react-router-dom";

/**
 * --- NOTE FOR PRODUCTION ---
 * Uncomment your original imports and remove these mocks in your local project.
 */
import Logo from "./homepage/images/logo.png";
import { useAuth } from "../hooks/useAuth";

/**
 * ForgotPasswordPage Component
 * Fixed: OTP auto-focus logic by moving handlers to main scope and preventing remounting.
 */
export default function App() {
    const { forgotPasswordRequest, verifyOTP, resetPassword } = useAuth();
    const navigate = useNavigate();

    // --- STATE ---
    const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password, 4: Success
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // OTP State
    const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
    const otpRefs = useRef([]);

    // --- OTP HANDLERS ---
    const handleOtpChange = (element, index) => {
        const value = element.value;
        if (isNaN(Number(value))) return false;

        const newOtp = [...otpArray];
        // Only take the last character entered
        newOtp[index] = value.substring(value.length - 1);
        setOtpArray(newOtp);

        // Auto-focus next field if a value was entered
        if (newOtp[index] !== "" && index < 5) {
            otpRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        // Handle Backspace
        if (e.key === "Backspace") {
            if (!otpArray[index] && index > 0) {
                // Focus previous field if current is empty
                otpRefs.current[index - 1].focus();
            }
        }
    };

    // --- FLOW HANDLERS ---
    const handleRequestOTP = async (e) => {
        e.preventDefault();
        if (!email) return setError("Please enter your email address.");
        setError("");
        setLoading(true);
        try {
            const resp = await forgotPasswordRequest({ email: email.toLowerCase() });
            if (resp?.code === 0) setStep(2);
            else setError(resp?.msg || "User not found or error occurred.");
        } catch (err) {
            setError("Request failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        const otpCode = otpArray.join("");
        if (otpCode.length < 6) return setError("Please enter the full 6-digit code.");
        setError("");
        setLoading(true);
        try {
            const resp = await verifyOTP({ email: email.toLowerCase(), otp: otpCode, type: "password_reset" });
            if (resp?.code === 0) setStep(3);
            else setError(resp?.msg || "Invalid or expired code.");
        } catch (err) {
            setError("Verification failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) return setError("Passwords do not match.");
        if (newPassword.length < 4) return setError("Password must be at least 6 characters.");
        setError("");
        setLoading(true);
        try {
            const resp = await resetPassword({
                email: email.toLowerCase(),
                otp: otpArray.join(""),
                newPassword
            });
            if (resp?.code === 0) setStep(4);
            else setError(resp?.msg || "Failed to reset password.");
        } catch (err) {
            setError("Reset failed.");
        } finally {
            setLoading(false);
        }
    };

    // --- RENDER HELPERS ---

    const renderStep1 = () => (
        <Box component="form" onSubmit={handleRequestOTP} sx={{ width: '100%', mt: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Enter your email address and we'll send you a code to reset your password.
            </Typography>
            <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
            />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Send Reset Code"}
            </Button>
        </Box>
    );

    const renderStep2 = () => (
        <Box sx={{ width: '100%', textAlign: 'center', mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                We've sent a 6-digit reset code to <br /> <b>{email}</b>
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 4 }}>
                {otpArray.map((data, index) => (
                    <TextField
                        key={index}
                        inputRef={(el) => (otpRefs.current[index] = el)}
                        value={data}
                        onChange={(e) => handleOtpChange(e.target, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        inputProps={{ 
                            maxLength: 1, 
                            style: { textAlign: 'center', fontWeight: 'bold', fontSize: '1.2rem' } 
                        }}
                        sx={{ width: '45px' }}
                    />
                ))}
            </Stack>
            <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyOTP}
                disabled={loading}
                sx={{ py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verify Code"}
            </Button>
            <Button fullWidth variant="text" sx={{ mt: 1 }} onClick={() => setStep(1)}>Back</Button>
        </Box>
    );

    const renderStep3 = () => (
        <Box component="form" onSubmit={handleResetSubmit} sx={{ width: '100%', mt: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Create a new secure password for your account.
            </Typography>
            <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                sx={{ mb: 2 }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <TextField
                fullWidth
                label="Confirm New Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, py: 1.5, fontWeight: 'bold', borderRadius: 2 }}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </Button>
        </Box>
    );

    const renderStep4 = () => (
        <Fade in={true}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>Password Reset!</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Your password has been updated successfully. You can now log in with your new credentials.
                </Typography>
                <Button fullWidth variant="contained" onClick={() => navigate("/login")} sx={{ py: 1.5, borderRadius: 2 }}>
                    Back to Login
                </Button>
            </Box>
        </Fade>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#fdfdfd', pb: 4 }}>
            <ArrowBackIcon
                sx={{
                    color: '#222',
                    m: 3,
                    border: '1px solid #222',
                    borderRadius: '50%',
                    fontSize: '32px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#eee' }
                }}
                onClick={() => {
                    if (step === 1 || step === 4) { navigate("/login"); }
                    else { setStep(step - 1); }
                }}
            />
            <Container maxWidth="xs">
                <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img src={Logo} alt="logo" width='120px' style={{ marginBottom: '20px' }} />

                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {step === 4 ? "Success" : "Forgot Password"}
                    </Typography>

                    {error && <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: 2 }}>{error}</Alert>}

                    {step === 1 && renderStep1()}
                    {step === 2 && renderStep2()}
                    {step === 3 && renderStep3()}
                    {step === 4 && renderStep4()}
                </Box>
            </Container>
        </Box>
    );
}